import { Box, Tooltip } from "@mui/material";
import {
  AttachFileOutlined,
  HomeWorkOutlined,
  SquareFootOutlined,
} from "@mui/icons-material";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { isConstructionProduct } from "../../../../../utils/loan/productType";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import CardPaper from "../../../../../components/ui/CardPaper";
import useAssociationUtils from "../../../../../components/associations/hooks/useAssociationUtils";
import { isDocHidden } from "../../../../../_src_shared/utils/functions/isDocHidden";
import {
  CONDITION_TONE,
  FLOOD_TONE_STYLES,
  FONT_MONO,
  FONT_SERIF,
  fmtMoney,
  getFloodCertChipState,
  SubTag,
  TOKENS,
} from "./propertyCardShared";

/**
 * PropertyCardCompact
 *
 * Dense counterpart to `PropertyCard`. Same data, same visual language, but
 * sized so a 15-property loan can be scanned in a couple of screens instead of
 * fifteen: no map, no files table, no associations editor — just address,
 * flags, the three headline values, and a one-line footer. Clicking still opens
 * the property detail sidebar, which is where the full record lives.
 *
 * Cards are laid out in a responsive grid by the tab, so this component just
 * fills its cell (height: 100%).
 */

// Flags beyond this count collapse into a "+N" pill so every card keeps the
// same two-line header height and the grid stays on a clean baseline.
const MAX_TAGS = 3;

const MiniFigure = ({ label, value, tone, borderRight }) => (
  <Box
    sx={{
      p: "8px 10px",
      minWidth: 0,
      flex: 1,
      borderRight: borderRight ? `1px solid ${TOKENS.line}` : "none",
    }}
  >
    <Txt
      sx={{
        display: "block",
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: TOKENS.ink3,
        fontWeight: 500,
        mb: "2px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        display: "block",
        fontFamily: FONT_MONO,
        fontSize: 13,
        fontWeight: 500,
        color: tone === "good" ? TOKENS.good : TOKENS.ink,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Txt>
  </Box>
);

const FooterStat = ({ icon, children, title }) => (
  <Tooltip title={title || ""} arrow disableHoverListener={!title}>
    <Flx ac g={0.5} sx={{ minWidth: 0, color: TOKENS.ink3 }}>
      {icon}
      <Txt
        sx={{
          fontSize: 11,
          color: TOKENS.ink3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </Txt>
    </Flx>
  </Tooltip>
);

const PropertyCardCompact = memo(({ property, isSelected, onClick }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // ARV is construction-only — hidden for DSCR.
  const construction = isConstructionProduct(loanDrilldown?.loanProductType);
  const { getAssociationDocumentsByPropertyId } = useAssociationUtils();

  const docCount = useMemo(
    () =>
      (
        getAssociationDocumentsByPropertyId(loanDrilldown, property?._id) || []
      ).filter((f) => !isDocHidden(f)).length,
    [getAssociationDocumentsByPropertyId, loanDrilldown, property?._id],
  );

  // ARV lift over as-is — the one derived number worth carrying into the
  // compact view, since it's the value-add story at a glance.
  const arvDelta = useMemo(() => {
    const arvNum = Number(property?.arv);
    const asIsNum = Number(property?.asIsValue);
    if (!Number.isFinite(arvNum) || !Number.isFinite(asIsNum) || asIsNum <= 0) {
      return null;
    }
    const pct = ((arvNum - asIsNum) / asIsNum) * 100;
    if (!Number.isFinite(pct) || pct === 0) return null;
    return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
  }, [property?.arv, property?.asIsValue]);

  const tags = useMemo(() => {
    const items = [];
    if (property?.propertyType) {
      items.push({ key: "type", label: property.propertyType, tone: "muted" });
    }
    if (
      property?.proposedPropertyType &&
      property.proposedPropertyType !== property?.propertyType
    ) {
      items.push({
        key: "proposed",
        label: `Proposed: ${property.proposedPropertyType}`,
        tone: "info",
      });
    }
    if (property?.floodZone) {
      items.push({ key: "flood-zone", label: "Flood zone", tone: "info" });
    }
    if (property?.changeOfUseFlag) {
      items.push({ key: "change-of-use", label: "Change of use", tone: "warn" });
    }
    if (property?.propertyListedForSale) {
      items.push({ key: "listed", label: "Listed for sale", tone: "urgent" });
    }
    if (property?.address?.manualEntry) {
      items.push({
        key: "manual-entry",
        label:
          property?.address?.geocodeQuality === "pin"
            ? "Manual · pinned"
            : "Manual · ZIP",
        tone: "warn",
      });
    }
    return items;
  }, [
    property?.propertyType,
    property?.proposedPropertyType,
    property?.floodZone,
    property?.changeOfUseFlag,
    property?.propertyListedForSale,
    property?.address?.manualEntry,
    property?.address?.geocodeQuality,
  ]);

  if (!property) return null;

  const {
    streetNumber,
    streetName,
    street,
    city,
    state,
    zip,
    fullAddress,
    addressUnitNumber,
  } = property?.address || {};
  const baseStreetLine =
    streetNumber && streetName
      ? `${streetNumber} ${streetName}`
      : street ||
        (fullAddress ? fullAddress.split(",")[0].trim() : null) ||
        "No address";
  // Unit/suite (e.g. "#734") is stored separately and not baked into
  // fullAddress — append it so the card shows the full unit address.
  const streetLine =
    addressUnitNumber && baseStreetLine !== "No address"
      ? `${baseStreetLine} ${addressUnitNumber}`
      : baseStreetLine;
  const cityStateLine =
    [[city, state].filter(Boolean).join(", "), zip].filter(Boolean).join(" ") ||
    "—";

  const condition = property?.propertyConditionAtOrigination;
  const conditionTone = condition ? CONDITION_TONE[condition] : null;
  const residentialSqFt = Number(property?.residentialSqFootage) || 0;

  const flood = getFloodCertChipState(property?.lastFloodCertResult);
  const floodStyles = FLOOD_TONE_STYLES[flood.tone] || FLOOD_TONE_STYLES.neutral;

  const visibleTags = tags.slice(0, MAX_TAGS);
  const overflowTags = tags.slice(MAX_TAGS);

  return (
    <CardPaper
      selected={isSelected}
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${TOKENS.line}`,
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          borderColor: TOKENS.ink4,
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      {/* HEADER — icon, street line, city/state, condition badge */}
      <Flx g={1.25} sx={{ p: "14px 14px 10px", alignItems: "flex-start" }}>
        <Flx
          ac
          jc="center"
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${TOKENS.surface2} 0%, ${TOKENS.accentSoft} 100%)`,
            border: `1px solid ${TOKENS.line}`,
          }}
        >
          <HomeWorkOutlined sx={{ fontSize: 17, color: TOKENS.accent }} />
        </Flx>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={fullAddress || streetLine} arrow>
            <Txt
              component="div"
              sx={{
                fontFamily: FONT_SERIF,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: "-0.015em",
                color: TOKENS.ink,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {streetLine}
            </Txt>
          </Tooltip>
          <Txt
            component="div"
            sx={{
              fontSize: 12,
              color: TOKENS.ink3,
              mt: "2px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cityStateLine}
          </Txt>
        </Box>

        {condition && (
          <Tooltip
            title="Property condition rating at origination (C1–C6)"
            arrow
          >
            <Box
              sx={{
                flexShrink: 0,
                px: "7px",
                py: "3px",
                borderRadius: "6px",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: FONT_MONO,
                bgcolor: conditionTone?.bg || TOKENS.surface2,
                color: conditionTone?.fg || TOKENS.ink2,
              }}
            >
              {condition}
            </Box>
          </Tooltip>
        )}
      </Flx>

      {/* FLAGS — capped so every card keeps the same header height */}
      {visibleTags.length > 0 && (
        <Flx ac wrap g={0.5} sx={{ px: "14px", pb: "10px" }}>
          {visibleTags.map((t) => (
            <SubTag key={t.key} tone={t.tone}>
              {t.label}
            </SubTag>
          ))}
          {overflowTags.length > 0 && (
            <Tooltip
              title={overflowTags.map((t) => t.label).join("\n")}
              arrow
              componentsProps={{
                tooltip: { sx: { whiteSpace: "pre-line" } },
              }}
            >
              <Box component="span" sx={{ display: "inline-flex" }}>
                <SubTag tone="muted">+{overflowTags.length}</SubTag>
              </Box>
            </Tooltip>
          )}
        </Flx>
      )}

      {/* FIGURES — the three headline values, connected-cell strip */}
      <Flx
        sx={{
          mx: "14px",
          background: TOKENS.surface3,
          border: `1px solid ${TOKENS.line}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <MiniFigure
          label="As-is"
          value={fmtMoney(property?.asIsValue)}
          borderRight
        />
        {construction && (
          <MiniFigure
            label={arvDelta ? `ARV ${arvDelta}` : "ARV"}
            value={fmtMoney(property?.arv)}
            tone={arvDelta && !arvDelta.startsWith("-") ? "good" : undefined}
            borderRight
          />
        )}
        <MiniFigure label="Purchase" value={fmtMoney(property?.purchasePrice)} />
      </Flx>

      {/* FOOTER — sq ft, docs, flood-cert state. `mt: auto` pins it to the
          bottom so cards in a row line their footers up even when one has
          fewer flag pills than its neighbours. */}
      <Flx
        ac
        jb
        g={1}
        sx={{
          mt: "auto",
          px: "14px",
          py: "10px",
          minWidth: 0,
        }}
      >
        <Flx ac g={1.5} sx={{ minWidth: 0 }}>
          <FooterStat
            icon={<SquareFootOutlined sx={{ fontSize: 14 }} />}
            title="Residential square footage"
          >
            {residentialSqFt > 0
              ? `${residentialSqFt.toLocaleString()} sq ft`
              : "—"}
          </FooterStat>
          <FooterStat
            icon={<AttachFileOutlined sx={{ fontSize: 14 }} />}
            title="Documents associated with this property"
          >
            {docCount}
          </FooterStat>
        </Flx>

        <Tooltip title={flood.label} arrow>
          <Flx
            ac
            g={0.5}
            sx={{
              flexShrink: 0,
              px: "7px",
              py: "2px",
              borderRadius: "10px",
              bgcolor: floodStyles.bg,
              border: `1px solid ${floodStyles.border}`,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: floodStyles.fg,
              }}
            />
            <Txt sx={{ fontSize: 10, fontWeight: 600, color: floodStyles.fg }}>
              Flood
            </Txt>
          </Flx>
        </Tooltip>
      </Flx>
    </CardPaper>
  );
});

export default PropertyCardCompact;
